import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Loader, Text } from 'src/@vymo/ui/atoms';
import { Form, Page } from 'src/@vymo/ui/molecules';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { getGrowthBookFeatureFlag } from 'src/featureFlags';
import { Keys, locale } from 'src/i18n';
import { getCurrentEntityId } from 'src/models/details/selectors';
import { ACTIVITY } from 'src/models/lmsConfig/constants';
import {
  getAllUsers,
  getHierarchy,
  getRevyreConfig,
  getUsers,
} from 'src/models/lmsConfig/selectors';
import { REVYRE_FORM } from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { removeDialog } from 'src/workspace/slice';
import { closePortal } from 'src/workspace/utils';
import {
  getEntityDetailsInputsMap,
  getNextEventAllowedAction,
  getProfileDetails,
} from '../details/selectors';
import { getCurrentWorkflowType, getIsLoading } from '../selectors';
import { fetchRevyreV1EntityDetails, formSubmit } from '../thunk';
import {
  getActivityUpdateDescription,
  getBodyPayloadForSubmit,
  getEntityIdForInputsMap,
  getEventId,
  getFormData,
  getFormTitle,
  getShowPageHeader,
  getWorkflowCode,
  normalizeSubmissionData,
} from './queries';
import {
  getFormInputsAndGroupsByEventId,
  getOutcomeValue,
  getStandardInputsAndGroups,
  getWorkflowsWithActivitiesTypes,
} from './selectors';
import { RevyreFormProps } from './types';
import styles from './index.module.scss';

function RevyreForm({
  type,
  domainType,
  workflowType,
  activityType,
  isBlockingScreen,
  buttonMetadata,
  showTitle = true,
}: RevyreFormProps) {
  const [formVersion, setFormVersion] = useState<number>(1);
  const formRef = useRef(null);
  const dispatch = useAppDispatch();
  const entityId = useAppSelector(getCurrentEntityId);
  const allUsers = useAppSelector(getAllUsers);
  const userHierarchy = useAppSelector(getHierarchy)?.user || {};
  const workflowConfig = useAppSelector(getRevyreConfig) || {};
  const users = useAppSelector(getUsers) || {};
  const isRevyreReturnCode = getGrowthBookFeatureFlag('revyre-returnCode');
  const workflowsWithActivitiesTypes = useAppSelector(
    getWorkflowsWithActivitiesTypes,
  );

  const selectedWorkflowAndActivity = useMemo<{
    workflowType?: string;
    activityType?: string;
  }>(() => {
    if (
      workflowsWithActivitiesTypes.length === 1 &&
      (workflowsWithActivitiesTypes[0]?.options || []).length === 1 &&
      domainType === ACTIVITY &&
      ['create', 'detailsCreate'].includes(type)
    ) {
      const [workflowCode, activityCode] =
        workflowsWithActivitiesTypes?.[0]?.options?.[0]?.id?.split?.('__') ||
        [];
      return {
        workflowType: workflowCode,
        activityType: activityCode,
      };
    }
    return {};
  }, [workflowsWithActivitiesTypes, domainType, type]);

  const [formData, setFormData] = useState<
    Record<string, { type: string; value: any }>
  >({});

  const effectiveEntityIdForInputs = getEntityIdForInputsMap(
    type,
    domainType,
    formData?.workflowId?.value,
    entityId,
  );

  const inputsMap = useAppSelector((state) =>
    getEntityDetailsInputsMap(state, effectiveEntityIdForInputs, {
      ...selectedWorkflowAndActivity,
      type,
      isBlockingScreen,
    }),
  );

  const currentPageWorkflowType = useAppSelector(getCurrentWorkflowType);
  const currentFormWorkflowType =
    workflowType || currentPageWorkflowType || inputsMap.workflowType;
  const isLoading = useAppSelector(getIsLoading);
  const outcomeValue = useMemo(() => getOutcomeValue(formData), [formData]);

  const nextEventActions = useAppSelector((state) =>
    getNextEventAllowedAction(
      state,
      getWorkflowCode(formData?.workflowId?.value),
      domainType,
    ),
  );

  const showPageHeader = useMemo(
    () => getShowPageHeader(type, showTitle),
    [type, showTitle],
  );

  const standardInputsAndGroups = useAppSelector((state) =>
    getStandardInputsAndGroups(
      type,
      domainType,
      entityId,
      formData,
      inputsMap,
      {
        workflowType: formData?.workflowType?.value ?? currentFormWorkflowType,
        eventId: formData?.selectState?.value,
        workFlowId: getWorkflowCode(formData.workflowId?.value),
        activityType: formData?.activityType?.value,
        isBlockingScreen,
        buttonMetadata,
      },
    )(state),
  );

  const profileDetails = useAppSelector((state) =>
    getProfileDetails(state, getWorkflowCode(formData.workflowId?.value)),
  );

  const formInputsAndGroupsByEventId = useAppSelector((state) =>
    getFormInputsAndGroupsByEventId(type, domainType, formData, inputsMap, {
      workflowType: formData?.workflowType?.value ?? currentFormWorkflowType,
      eventId: getEventId({ buttonMetadata, formData, type }),
      workFlowId: getWorkflowCode(formData.workflowId?.value),
      outcomeId: outcomeValue,
      isBlockingScreen,
      buttonMetadata,
      standardInputs: standardInputsAndGroups?.inputs,
    })(state),
  );

  useEffect(() => {
    setFormData(
      getFormData(
        inputsMap,
        formData,
        domainType,
        currentFormWorkflowType,
        activityType,
        effectiveEntityIdForInputs,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(inputsMap)]);

  const onFormChange = (formFields) => {
    const {
      activityType: formActivityType,
      workflowType: formWorkflowType = {},
      workflowId: formWorkflowId = {},
    } = formFields;

    if (
      ['create', 'createGlobal', 'update'].includes(type) &&
      formWorkflowId?.value &&
      formData.workflowId?.value !== formWorkflowId?.value &&
      domainType !== 'workflow'
    ) {
      dispatch(
        fetchRevyreV1EntityDetails({
          domainType: 'workflow',
          workflowType: formWorkflowType?.value,
          entityId: getWorkflowCode(formWorkflowId?.value),
          type,
        }),
      );
    }

    if (
      domainType === 'activity' &&
      type === 'create' &&
      formActivityType?.additionalData?.data?.id
    ) {
      formWorkflowType.value =
        formActivityType?.additionalData?.data?.id?.split?.('__')?.[0] ?? '';
      setFormData({
        ...formFields,
        workflowType: formWorkflowType,
      });
    } else {
      setFormData(formFields);
    }
  };

  const onFormSubmit = async () => {
    // @ts-ignore
    const result = await formRef.current?.getFieldsForSubmission?.();
    if (Array.isArray(result?.data) && isRevyreReturnCode) {
      result.data = normalizeSubmissionData(result.data);
    }
    if (result?.valid === 'valid') {
      const payload = getBodyPayloadForSubmit(
        type,
        result,
        standardInputsAndGroups.inputs,
        domainType,
        currentFormWorkflowType,
        inputsMap?.id,
      );

      const formEntityId = getWorkflowCode(formData.workflowId?.value);
      const formEventId =
        formData.selectState?.value || buttonMetadata?.customUpdateState;
      const resolvedEntityId =
        domainType === 'workflow' ? formEntityId ?? entityId : entityId;

      dispatch(
        formSubmit({
          type,
          domainType,
          workflowType: formData.workflowType?.value,
          entityId: resolvedEntityId,
          eventId: formEventId,
          payload,
          isBlockingScreen,
          buttonMetadata,
        }),
      );
    }
  };

  const formInputsConfig = [
    ...standardInputsAndGroups.inputs,
    ...(formInputsAndGroupsByEventId?.inputs || []),
  ];

  useEffect(() => {
    setFormVersion((prev) => prev + 1);
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formInputsConfig),
  ]);

  return (
    <Page className={styles.formPage}>
      {showPageHeader && (
        <Page.Header className={styles.formPage__header}>
          <Text bold type="h5" data-test-id={`${type}-title`}>
            {getFormTitle(
              type,
              currentFormWorkflowType,
              domainType,
              inputsMap?.name,
            )}
          </Text>
        </Page.Header>
      )}
      <Page.Body>
        <Loader visible={isLoading} />
        <Form
          key={formVersion}
          ref={formRef}
          description={getActivityUpdateDescription({
            domainType,
            type,
            activityType,
            inputsMap,
          })}
          id="actionForm"
          name="actionForm"
          className={styles.actionForm__form}
          config={{
            version: FormVersion.webRevyre,
            data: formInputsConfig,
            grouping: [
              ...(standardInputsAndGroups?.groups || []),
              ...(formInputsAndGroupsByEventId?.groups || []),
            ],
            fieldItemConfig: {
              showDisabledIcon: true,
            },
            customPayload: {
              entityNextEventOptions: nextEventActions,
            },
            viewMode: false,
            metaData: {
              allUsers,
              userHierarchy,
              workflowConfig,
              users,
              profileDetails,
            },
          }}
          onChange={onFormChange}
          value={formData as any}
        />
      </Page.Body>
      <Page.Footer className={styles.formPage__footer}>
        {!isBlockingScreen && (
          <Button
            type="text"
            onClick={() => {
              dispatch(removeDialog({ id: REVYRE_FORM }));
              if (type === 'createGlobal') {
                closePortal?.();
              }
            }}
          >
            {locale(Keys.CANCEL)}
          </Button>
        )}
        <Button
          onClick={onFormSubmit}
          data-test-id={`${type}-${domainType}-submit`}
        >
          {locale(Keys.SUBMIT)}
        </Button>
      </Page.Footer>
    </Page>
  );
}

export default RevyreForm;
